/**
 * Admin Page - Manage device templates
 * 
 * Access: /admin?key=dev-secret-123
 * Features:
 * - Review pending user-submitted templates
 * - Edit built-in templates
 * - Add new templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Heading, Text, Button, Tabs, Flex, Badge, Card, Grid } from '@radix-ui/themes';
import { HomeIcon } from '@radix-ui/react-icons';
import { DeviceTemplate } from '../types';
import { adminAPI, templatesAPI, analyticsAPI, AnalyticsStats } from '../services/api';
import AdminReview from '../components/AdminReview';
import TemplateLibrary from '../components/TemplateLibrary';
import AddTemplate from '../components/AddTemplate';
import TemplateImportExport from '../components/TemplateImportExport';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminKey = searchParams.get('key');
  
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allTemplates, setAllTemplates] = useState<DeviceTemplate[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [analyticsStats, setAnalyticsStats] = useState<AnalyticsStats | null>(null);

  // Verify admin key on mount
  useEffect(() => {
    const verifyAccess = async () => {
      if (!adminKey || (adminKey !== 'dev-secret-123' && adminKey !== process.env.REACT_APP_ADMIN_KEY)) {
        navigate('/');
        return;
      }
      
      try {
        // Try to fetch pending templates to verify key works
        await adminAPI.getPendingTemplates(adminKey);
        setAuthorized(true);
      } catch (error) {
        console.error('Admin authentication failed:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    verifyAccess();
  }, [adminKey, navigate]);

  const loadTemplates = useCallback(async () => {
    if (!adminKey) return;
    
    try {
      const [pending, all, analytics] = await Promise.all([
        adminAPI.getPendingTemplates(adminKey),
        templatesAPI.getAll(),
        analyticsAPI.getStats(adminKey).catch(() => null) // Gracefully handle analytics errors
      ]);
      
      setPendingCount(pending.length);
      setAllTemplates(all);
      setAnalyticsStats(analytics);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  }, [adminKey]);

  useEffect(() => {
    if (authorized) {
      loadTemplates();
    }
  }, [authorized, loadTemplates]);

  if (loading) {
    return (
      <Container size="2" p="4">
        <Text>Verifying credentials...</Text>
      </Container>
    );
  }

  if (!authorized) {
    return null; // Redirecting
  }

  return (
    <Container size="4" p="4">
      <Flex justify="between" align="center" mb="4">
        <Heading size="8">🔒 Admin Panel</Heading>
        <Button variant="soft" onClick={() => navigate('/')}>
          <HomeIcon /> Back to App
        </Button>
      </Flex>

      <Text size="2" color="gray" mb="4">
        Manage device templates and review user submissions
      </Text>

      <Tabs.Root defaultValue="stats">
        <Tabs.List>
          <Tabs.Trigger value="stats">
            📊 Dashboard
          </Tabs.Trigger>
          <Tabs.Trigger value="pending">
            Pending Review
            {pendingCount > 0 && (
              <Badge color="orange" ml="2">{pendingCount}</Badge>
            )}
          </Tabs.Trigger>
          <Tabs.Trigger value="library">
            Template Library
            <Badge color="blue" ml="2">{allTemplates.length}</Badge>
          </Tabs.Trigger>
          <Tabs.Trigger value="add">
            + Add New
          </Tabs.Trigger>
          <Tabs.Trigger value="import-export">
            📦 Import/Export
          </Tabs.Trigger>
        </Tabs.List>

        <Box pt="4">
          <Tabs.Content value="stats">
            {!analyticsStats ? (
              <Text>Loading analytics...</Text>
            ) : (
              <>
                <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="4">
                  <Card>
                    <Flex direction="column" gap="2">
                      <Text size="2" color="gray" weight="medium">Total Rigs Created</Text>
                      <Heading size="8" color="violet">{analyticsStats.rigsCreated}</Heading>
                      <Text size="1" color="gray">Across all users</Text>
                    </Flex>
                  </Card>

                  <Card>
                    <Flex direction="column" gap="2">
                      <Text size="2" color="gray" weight="medium">Total Devices Added</Text>
                      <Heading size="8" color="blue">{analyticsStats.devicesAdded}</Heading>
                      <Text size="1" color="gray">All devices configured</Text>
                    </Flex>
                  </Card>

                  <Card>
                    <Flex direction="column" gap="2">
                      <Text size="2" color="gray" weight="medium">Approved Templates</Text>
                      <Heading size="8" color="green">{allTemplates.filter(t => t.status === 'approved').length}</Heading>
                      <Text size="1" color="gray">Available in library</Text>
                    </Flex>
                  </Card>

                  <Card>
                    <Flex direction="column" gap="2">
                      <Text size="2" color="gray" weight="medium">Unique Sessions</Text>
                      <Heading size="8" color="cyan">{analyticsStats.uniqueSessions}</Heading>
                      <Text size="1" color="gray">Total unique visitors</Text>
                    </Flex>
                  </Card>

                  <Card>
                    <Flex direction="column" gap="2">
                      <Text size="2" color="gray" weight="medium">Active (24h)</Text>
                      <Heading size="8" color="amber">{analyticsStats.activeSessions24h}</Heading>
                      <Text size="1" color="gray">Sessions last 24 hours</Text>
                    </Flex>
                  </Card>

                  <Card>
                    <Flex direction="column" gap="2">
                      <Text size="2" color="gray" weight="medium">Pending Review</Text>
                      <Heading size="8" color="orange">{pendingCount}</Heading>
                      <Text size="1" color="gray">Awaiting approval</Text>
                    </Flex>
                  </Card>
                </Grid>

                <Grid columns={{ initial: '1', md: '2' }} gap="4" mt="4">
                  <Card>
                    <Heading size="4" mb="3">Event Breakdown</Heading>
                    <Flex direction="column" gap="2">
                      {analyticsStats.eventsByType.slice(0, 8).map((event) => (
                        <Flex key={event.event_type} justify="between">
                          <Text size="2" color="gray">{event.event_type.replace(/_/g, ' ')}:</Text>
                          <Badge color="blue">{event.count}</Badge>
                        </Flex>
                      ))}
                    </Flex>
                  </Card>

                  <Card>
                    <Heading size="4" mb="3">Quick Stats</Heading>
                    <Flex direction="column" gap="2">
                      <Flex justify="between">
                        <Text size="2" color="gray">Total events tracked:</Text>
                        <Text size="2" weight="bold">{analyticsStats.totalEvents}</Text>
                      </Flex>
                      <Flex justify="between">
                        <Text size="2" color="gray">Avg devices per rig:</Text>
                        <Text size="2" weight="bold">
                          {analyticsStats.rigsCreated > 0 
                            ? (analyticsStats.devicesAdded / analyticsStats.rigsCreated).toFixed(1) 
                            : '0'}
                        </Text>
                      </Flex>
                      <Flex justify="between">
                        <Text size="2" color="gray">Template library size:</Text>
                        <Text size="2" weight="bold">{allTemplates.length} templates</Text>
                      </Flex>
                      <Flex justify="between">
                        <Text size="2" color="gray">Built-in templates:</Text>
                        <Text size="2" weight="bold">{allTemplates.filter(t => t.isBuiltIn).length}</Text>
                      </Flex>
                      <Flex justify="between">
                        <Text size="2" color="gray">User contributed:</Text>
                        <Text size="2" weight="bold">
                          {allTemplates.filter(t => !t.isBuiltIn && t.status === 'approved').length}
                        </Text>
                      </Flex>
                    </Flex>
                  </Card>
                </Grid>

                {analyticsStats.recentActivity.length > 0 && (
                  <Card mt="4">
                    <Heading size="4" mb="3">Recent Activity (Last 7 Days)</Heading>
                    <Flex direction="column" gap="2">
                      {analyticsStats.recentActivity.map((day) => (
                        <Flex key={day.date} justify="between">
                          <Text size="2" color="gray">{new Date(day.date).toLocaleDateString()}:</Text>
                          <Flex gap="3">
                            <Badge color="blue">{day.events} events</Badge>
                            <Badge color="violet">{day.sessions} sessions</Badge>
                          </Flex>
                        </Flex>
                      ))}
                    </Flex>
                  </Card>
                )}
              </>
            )}
          </Tabs.Content>

          <Tabs.Content value="pending">
            {adminKey && (
              <AdminReview 
                adminKey={adminKey} 
                onClose={() => {}} 
                onUpdate={loadTemplates}
              />
            )}
          </Tabs.Content>

          <Tabs.Content value="library">
            {adminKey && (
              <TemplateLibrary 
                adminKey={adminKey}
                onUpdate={loadTemplates}
              />
            )}
          </Tabs.Content>

          <Tabs.Content value="add">
            {adminKey && (
              <AddTemplate
                adminKey={adminKey}
                onSuccess={loadTemplates}
              />
            )}
          </Tabs.Content>

          <Tabs.Content value="import-export">
            {adminKey && (
              <TemplateImportExport
                adminKey={adminKey}
                onUpdate={loadTemplates}
              />
            )}
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </Container>
  );
};

export default AdminPage;
